<template>
  <div class="home map-obstruction" ref="obstruction">
    <TopBox>
      <div class="top">
        <NoticeInfo :currentArea="currentArea" />
        <UserCenter class="user" />
        <ActivityEntry class="activity" />
      </div>
    </TopBox>

    <div class="card">
      <CardFloat>
        <div v-if="isUndefinedOrder" class="unfinished-order-box">
          <!-- 未完成订单 -->
          <UnfinishedOrder :orderInfo="orderTicketInfo" />
          <!-- 定位 -->
          <MapActions class="unfinished-order-map-actions" />
        </div>
        <div
          v-else
          :class="{
            'direct-call-col': isExpandDirectCall,
            'direct-call-row': !isExpandDirectCall,
          }"
        >
          <!-- 定位 -->
          <MapActions class="direct-call-map-actions" />
          <!-- 一键叫车 -->
          <DirectCallEntry v-model="isExpandDirectCall" />
        </div>
      </CardFloat>
      <div class="row">
        <!-- 区域选择 -->
        <AreaSelector :currentArea="currentArea" @select="onClickAreaTrigger" />
        <!-- 乘车指引 -->
        <BusGuide :ticketConfig="ticketConfig" />
      </div>

      <!-- 区域描述 -->
      <AreaDesc :currentArea="currentArea" />
      <!-- 上/下站选择 -->
      <StopSelector
        :onStop="onStop"
        :offStop="offStop"
        :isHubArea="isHubArea"
        :isFromHub="isFromHub"
        :isReversedExist="isReversedExist"
        @on-stop="onOnPoiClick"
        @off-stop="onOffPoiClick"
        @switch-direction="onSwitchDirection"
      />

      <!-- 乘车属性 -->
      <div class="menu">
        <TimeSelector v-model="bookingDateTime" style="flex: 1" />
        <PassengerSelector
          v-model="passengerIndex"
          :columns="passengerPickerData"
          @confirm="onPassengerOk"
          style="flex: 1"
        />
      </div>
      <!-- 下单按钮 -->
      <VanButton v-if="isProtocol" class="submit" @click="onClickSubmit">
        下单
      </VanButton>
      <VanButton v-else class="submit disabled" @click="onClickSubmitDisabled">
        下单
      </VanButton>
      <!-- 协议 -->
      <ProtocolSelector v-model="isProtocol" :ticketConfig="ticketConfig" />
    </div>
    <!-- 选择区域 -->
    <AreaPicker
      v-model="areaPickerVisible"
      :default-index="currentAreaIndex"
      :columns="areaPickerList"
      @confirm="onAreaPickerOk"
      @cancel="onAreaPickerCancel"
    />
    <!-- 支付弹框 -->
    <CashCardPopup v-model="cashCardVisible">
      <CashCard
        v-if="cashCardVisible"
        :coupons="couponList"
        :cashParams="cashParams"
        :isChartered="isChartered"
        :groupPrice="currentArea.groupPrice"
        @onChoose="onChooseCoupon"
        @onOrder="onConfirmOrder"
        @useCouponFail="useCouponFail"
      />
    </CashCardPopup>
  </div>
</template>

<script>
import UserCenter from "@/components/user-center-trigger-v2/index.vue";
import UnfinishedOrder from "./components/UnfinishedOrder.vue";
import TimeSelector from "./components/TimeSelector.vue";
import AreaSelector from "./components/AreaSelector.vue";
import AreaDesc from "./components/AreaDesc.vue";
import StopSelector from "./components/StopSelector.vue";
import PassengerSelector from "./components/PassengerSelector.vue";
import ProtocolSelector from "./components/ProtocolSelector.vue";
import AreaPicker from "./components/AreaPicker.vue";
import TopBox from "./components/TopBox.vue";
import NoticeInfo from "./components/NoticeInfo.vue";
import ActivityEntry from "./components/ActivityEntry.vue";
import MapActions from "./components/MapActions.vue";
import DirectCallEntry from "./components/DirectCallEntry.vue";
import CardFloat from "./components/CardFloat.vue";
import CashCardPopup from "./components/CashCardPopup.vue";
import CashCard from "./components/CashCard.vue";
import BusGuide from "./components/BusGuide.vue";

import {
  wait,
  getPayType,
  isAndroid,
  baiduToMars,
  deepCopy,
} from "@/utils/index";
import { AREA_MODE, AREA_TICKET_TYPE } from "@/const/area";
import { GROUP_COL } from "@/const/group";
import {
  getPassengerPickerData,
  getDateAndTime,
  findStop,
} from "@/views/home/util";
import { getValidCoupons } from "@/views/coupon/mgr/index";
import { mapGetters, mapMutations } from "vuex";
import { Toast } from "vant";

const DO_NOT_USE_COUPON = "no";

export default {
  name: "Home",
  components: {
    UserCenter,
    UnfinishedOrder,
    AreaSelector,
    AreaDesc,
    StopSelector,
    TimeSelector,
    PassengerSelector,
    ProtocolSelector,
    AreaPicker,
    TopBox,
    NoticeInfo,
    ActivityEntry,
    MapActions,
    DirectCallEntry,
    CardFloat,
    CashCardPopup,
    CashCard,
    BusGuide,
  },
  data() {
    return {
      passengerIndex: 0,
      ordering: false,
      orderTicketInfo: null, // 正在进行/未支付的订单
      isProtocol: false,
      couponList: [], // 可用优惠劵列表
      orderParams: null, // 下单前的参数（不包括优惠劵）
      cashParams: {
        selectedCouponId: null, // 选择的优惠劵id
        actualPrice: 0, // 未计算优惠劵前的实际价格
      },
      cashCardVisible: false,
      bookingDateTime: "", // 预约时间段 格式：`${date}__${time}`
      isExpandDirectCall: false,
    };
  },
  computed: {
    ...mapGetters({
      onStop: "dataOnStop",
      offStop: "dataOffStop",
      areaCenterPos: "dataAreaCenterPos",
      areas: "run/areas",
      currentArea: "run/currentArea",
      areaId: "home/areaId",
      areaPickerVisible: "home/areaPickerVisible",
      currentAreaName: "run/currentAreaName",
      areaPickerList: "run/areaPickerList",
      currentAreaIndex: "run/currentAreaIndex",
      currentAreaPriceList: "run/currentAreaPriceList",
      ticketConfig: "config/ticketConfig",
      userPos: "dataUserPos",
      isHubArea: "run/isHubArea", // 是否枢纽站模式
      isBookingTicket: "run/isBookingTicket", // 是否预约单
      isFromHub: "run/isFromHub", // 是否从枢纽站出发
      isReversedExist: "run/isReversedExist", // 是否存在反方向
      hubStops: "run/hubStops", // 枢纽站站点
    }),
    onStopId() {
      const { id } = this.onStop || {};
      return id;
    },
    offStopId() {
      const { id } = this.offStop || {};
      return id;
    },
    onStopName() {
      const { name } = this.onStop || {};
      return name || "您在哪儿上车";
    },
    offStopName() {
      const { name } = this.offStop || {};
      return name || "输入您的目的地";
    },
    cityId() {
      return this.$AppService.getCityId() || this.$route.query.cityId;
    },
    passengerPickerData() {
      let { groupPrice, userNum, priceMode, distancePriceList, minUserNum } =
        this.currentArea || {};
      let group = false;
      if (priceMode === 1) {
        group = !!(
          distancePriceList &&
          distancePriceList.length &&
          distancePriceList[0] &&
          typeof distancePriceList[0].groupPrice === "number"
        );
      } else {
        group = typeof groupPrice === "number";
      }
      const res = [
        ...getPassengerPickerData({ userNum: userNum || 1, minUserNum }),
      ];
      if (group) {
        res.push({ ...GROUP_COL });
      }
      return res;
    },
    passengerCount() {
      const { value } = this.passengerPickerData[this.passengerIndex] || {};
      return value;
    },
    // 是否选中包车
    isChartered() {
      const { type } = this.passengerPickerData[this.passengerIndex] || {};
      return type === GROUP_COL.type;
    },
    disabledOnStop() {
      return this.isHubArea && this.isFromHub;
    },
    disabledOffStop() {
      return this.isHubArea && !this.isFromHub;
    },
    isUndefinedOrder() {
      const { undefinedNum } = this.orderTicketInfo || {};
      return undefinedNum > 0;
    },
  },
  watch: {
    areaId: {
      immediate: true,
      handler() {
        this.setDefaultPassenger();
        this.handleAreaChange();
      },
    },
    onStop: {
      immediate: true,
      deep: true,
      handler(onStop) {
        if (onStop && !this.offStop) {
          // 允许地图拖拽选取上车站
          if (this.disabledOnStop) {
            this.$MapService.setStateOnStopDraggable(false);
          } else {
            this.$MapService.setStateOnStopDraggable(true);
          }
          // 禁止地图拖拽选取下车站
          this.$MapService.setStateOffStopDraggable(false);
        }
        if (onStop && this.offStop) {
          // 禁止地图拖拽选取上车站
          this.$MapService.setStateOnStopDraggable(false);
          // 禁止地图拖拽选取下车站
          this.$MapService.setStateOffStopDraggable(false);
        }
      },
    },
    ordering: {
      immediate: true,
      handler(ordering) {
        if (ordering) {
          Toast.loading({
            message: "正在下单",
            duration: 0,
            forbidClick: true,
          });
        } else {
          Toast.clear();
        }
      },
    },
  },
  beforeRouteEnter(to, from, next) {
    const { name, params } = from || {};
    // 首页跳转活动列表页再跳转领劵页面（非本系统页面），安卓使用物理按键返回首页，地图不渲染了
    // 原因：可能安卓缓存页面的缘故？？？
    // 解决方案：地图和数据请求都写在全局组件App.vue中，只能暂时在这里强制刷新页面
    if (name === "activityList" && isAndroid()) {
      window.location.reload();
      return;
    }
    if (name === "choose") {
      const { couponId } = params || {};
      next((vm) => {
        vm.cashCardVisible = true;
        setTimeout(() => {
          vm.cashParams = {
            ...vm.cashParams,
            selectedCouponId: couponId || null,
          };
        });
        // 枢纽站模式下，重新进入首页，需要默认选中枢纽站
        vm.handleAreaChange(false);
      });
    } else {
      next((vm) => {
        vm.cashCardVisible = false;
        // 枢纽站模式下，重新进入首页，需要默认选中枢纽站
        vm.handleAreaChange(false);
      });
    }
  },
  activated() {
    this.setHideUserPosCtrl(true);
    this.$nextTick(() => {
      const [lng, lat] = this.userPos || [];
      const marsPos = baiduToMars(lng, lat);
      this.$HomeService
        .fetchAreas({ lng: marsPos.lng, lat: marsPos.lat })
        .then((data) => {
          if (!data || !data.length) {
            this.$cheToast({
              type: "fail",
              text: "当前暂无运营区域",
            });
          }
          this.$RunService.setAreas(data);
        });
      if (!this.onStop && !this.offStop) {
        this.$MapService.setMapFitViewModeA();
      } else if (this.onStop && !this.offStop) {
        this.$MapService.setMapFitViewModeD();
      } else if (this.onStop && this.offStop) {
        this.$MapService.setMapFitViewModeA();
      } else {
        this.$MapService.setMapFitViewModeA();
      }
      this.$AppService.setTicketInfoId(null);
      this.$MapService.setMapModeNormal();
      this.$MapService.setStateMapVisible(true);
      this.$MapService.observeMapObstruction(this.$refs.obstruction);
      this.$MapService.setMapRenderFlag();
      if (this.onStop && !this.offStop) {
        if (this.disabledOnStop) {
          this.$MapService.setStateOnStopDraggable(false);
        } else {
          this.$MapService.setStateOnStopDraggable(true);
        }
      }
      wait(1000).then(() => {
        this.$OrderService
          .recoverRunningOrder()
          .then((res) => {
            this.orderTicketInfo = res;
          })
          .catch((e) => {
            this.orderTicketInfo = null;
            console.log("---", e);
          });
      });
    });
  },
  deactivated() {
    this.setHideUserPosCtrl(false);
    this.setRemoveMarkerLabel(true);
  },
  methods: {
    ...mapMutations({
      setHideUserPosCtrl: "setHideUserPosCtrl",
      setRemoveMarkerLabel: 'setRemoveMarkerLabel'
    }),
    preCheckUserLogin() {
      if (this.$UserService.isUserLogin()) {
        return true;
      }
      this.$UserService.turnToLoginView();
      return false;
    },
    hidePage() {
      let el = this.$refs.obstruction;
      if (el) {
        el.style.display = "none";
        wait(1000).then(() => {
          el.style.display = "block";
          this.$MapService.observeMapObstruction(this.$refs.obstruction);
        });
      }
    },
    async onOnPoiClick() {
      if (this.disabledOnStop) return;
      if (!(await this.preCheckUserLogin())) return;
      this.hidePage();
      this.$PoiService.setStopTypeOn();
      this.$nextTick(() => {
        this.$PoiService.turnToPoiView(
          {
            from: "Home",
          },
          {
            type: "on",
            areaId: this.areaId,
          }
        );
      });
    },
    async onOffPoiClick() {
      if (this.disabledOffStop) return;
      if (!(await this.preCheckUserLogin())) return;
      if (!this.onStop || !this.onStopId) {
        this.$toast("请先选择上车站");
        return;
      }
      this.hidePage();
      this.$PoiService.setStopTypeOff();
      this.$nextTick(() => {
        this.$PoiService.turnToPoiView(
          {
            from: "Home",
          },
          {
            type: "off",
            areaId: this.areaId,
          }
        );
      });
    },
    onClickSubmit() {
      if (!this.onStopId || !this.offStopId) {
        this.$toast({
          message: "请选择站点",
        });
        return;
      }
      const { date: useBookingDate, time: useBookingTimeRange } =
        getDateAndTime(this.bookingDateTime) || {};
      if (
        this.isHubArea &&
        this.isBookingTicket &&
        (!useBookingDate || !useBookingTimeRange)
      ) {
        this.$toast({
          message: "选择出行时间",
        });
        return;
      }
      if (!this.$UserService.isUserLogin()) {
        this.$UserService.turnToLoginView();
        return;
      }
      if (!this.ordering) {
        this.ordering = true;
        const { id, stops, groupPrice } = this.currentArea || {};
        const onStop = findStop(this.onStopId, {
          isHubArea: this.isHubArea,
          innerStops: stops,
          hubStops: this.hubStops,
        });
        const offStop = findStop(this.offStopId, {
          isHubArea: this.isHubArea,
          innerStops: stops,
          hubStops: this.hubStops,
        });
        if (this.onStopId === this.offStopId) {
          this.$toast({
            message: "上下车站点不能相同",
          });
          this.ordering = false;
          return;
        }
        const { originLng: onLng, originLat: onLat } = onStop || {};
        const { originLng: offLng, originLat: offLat } = offStop || {};
        const onStopLocation = onLng && onLat ? `${onLng},${onLat}` : "";
        const offStopLocation = offLng && offLat ? `${offLng},${offLat}` : "";
        this.$HomeService
          .fetchPrice(
            {
              baseLineId: id,
              onStopId: onStop ? onStop.id : null,
              onStopName: onStop ? onStop.name : null,
              offStopId: offStop ? offStop.id : null,
              offStopName: offStop ? offStop.name : null,
              ticketNum: this.passengerCount,
              userId: this.$UserService.getUserId(),
              payType: getPayType().id,
              group: this.isChartered,
            },
            this
          )
          .then(({ originPrice, actualPrice }) => {
            let params = {
              baseLineId: this.areaId,
              offStopId: this.offStopId,
              offStopName: this.offStopName,
              onStopId: this.onStopId,
              onStopName: this.onStopName,
              payType: getPayType().id,
              singalNum: this.passengerCount, //  乘客数量
              ticketPrice: originPrice, // 原价
              sessionId: this.$UserService.getSessionId(),
              mobile: this.$UserService.getUserMobile(),
              actualPrice,
              groupPrice: this.isChartered ? groupPrice : undefined,
              isChartered: this.isChartered,
            };
            // 枢纽站模式下单新增参数
            const hubParams = {
              mode: this.isHubArea ? AREA_MODE.HUB : AREA_MODE.INNEAR,
              onStopLocation,
              offStopLocation,
              ticketType: this.isBookingTicket
                ? AREA_TICKET_TYPE.BOOKING_ORDER
                : AREA_TICKET_TYPE.REALTIME_ORDER,
              useBookingDate,
              useBookingTimeRange,
            };
            if (this.isHubArea) {
              params = { ...params, ...hubParams };
            }
            const [userLng, userLat] = this.userPos || [];
            if (userLng && userLat) {
              const marsPos = baiduToMars(userLng, userLat) || {};
              params.lng = marsPos.lng;
              params.lat = marsPos.lat;
            }
            console.log("onClickSubmit params: ", params);
            this.checkOrder(params);
          })
          .catch((error) => {
            const { msg } = typeof error === "object" && error ? error : {};
            this.ordering = false;
            this.cashCardVisible = false;
            this.$cheToast({
              type: "fail",
              text: msg || "下单失败",
              timeout: 3000,
            }).show();
          });
      }
    },
    // 下单前进行校验：当前有可用优惠劵，需要弹框；没有可用优惠劵，直接下单
    checkOrder(params) {
      const { actualPrice } = params || {};
      const isZeroPay = typeof actualPrice === "number" && actualPrice <= 0; // 零元购
      if (isZeroPay || this.isChartered) {
        this.handleOrder(params);
        return;
      }
      // 有可用优惠劵，先弹框选劵
      this.requestValidCoupons(actualPrice)
        .then(() => {
          this.ordering = false;
          this.orderParams = params;
          const data = { ...this.cashParams };
          this.cashParams = null;
          setTimeout(() => {
            this.cashParams = {
              ...data,
              ...deepCopy(params),
            };
          }, 10);
          console.log("orderParams", this.orderParams);
          this.cashCardVisible = true;
        })
        .catch(() => {
          // 没有可用优惠劵，直接下单
          this.handleOrder(params);
        });
    },
    // 确认下单
    onConfirmOrder(data) {
      const { couponId, actualPrice } = data || {};
      if (couponId && couponId !== DO_NOT_USE_COUPON) {
        this.orderParams.couponId = couponId;
      } else {
        delete this.orderParams.couponId;
      }
      if (actualPrice || actualPrice === 0) {
        this.orderParams.actualPrice = actualPrice;
      }
      this.handleOrder(this.orderParams);
    },
    // 设置默认的乘车人数
    setDefaultPassenger() {
      this.passengerIndex = 0;
    },
    // 进行下单
    handleOrder(params = {}) {
      // 包车不支持优惠劵
      if (this.isChartered) {
        delete params.couponId;
      }
      this.ordering = true;
      this.$AppService
        .order(params)
        .then((res) => {
          this.$AppService.setWxPayInfo(res);
          this.$MapService.setMapDataOnStop(null);
          this.$MapService.setMapDataOffStop(null);
          this.setDefaultPassenger();
          wait(500).then(() => {
            this.$OrderService.turnToOrderPay(
              {
                from: "Home",
              },
              {
                ticketInfoId: this.$AppService.getTicketInfoId(),
              }
            );
          });
          this.ordering = false;
          this.cashCardVisible = false;
          this.clearOrderCache();
        })
        .catch((res) => {
          this.ordering = false;
          this.$nextTick(() => {
            this.$cheToast({
              type: "fail",
              text: res.msg || res.errmsg,
              timeout: 3000,
            }).show();
          });
        });
    },
    // 下单成功后，清理下单前的缓存数据
    clearOrderCache() {
      this.orderParams = null;
      this.cashParams = {
        selectedCouponId: null,
        actualPrice: 0,
      };
    },
    onPassengerOk(index) {
      this.passengerIndex = index;
    },
    onClickAreaTrigger() {
      if (!this.areaPickerList.length) {
        this.$toast("当前暂无运营区域");
        return;
      }
      this.$HomeService.setAreaPickerVisible(true);
    },
    onAreaPickerCancel() {
      this.$HomeService.setAreaPickerVisible(false);
    },
    onAreaPickerOk(value, index) {
      this.$HomeService.setAreaPickerVisible(false);
      this.$MapService.setMapFitViewModeA();
      if (index !== this.currentAreaIndex) {
        this.$RunService.setCurrentArea(index);
      }
    },
    onClickSubmitDisabled() {
      this.$toast("请先阅读并同意下方协议");
    },
    // 请求获取可用优惠劵
    requestValidCoupons(price) {
      const userId = this.$UserService.getUserId() || "";
      const lineId = this.areaId;
      return getValidCoupons({
        bigCityId: this.cityId,
        cityId: this.cityId,
        userId,
        lineId,
        price,
      }).then((res) => {
        this.couponList = res || [];
        if (res && res.length) {
          this.cashParams = {
            ...this.cashParams,
            selectedCouponId: res[0].couponId,
          };
          return Promise.resolve(res);
        } else {
          this.cashParams = {
            ...this.cashParams,
            selectedCouponId: null,
          };
          return Promise.reject();
        }
      });
    },
    // 去往选择优惠劵页面
    onChooseCoupon() {
      if (this.$UserService.isUserLogin()) {
        const params = this.cashParams.selectedCouponId
          ? {
              couponId: this.cashParams.selectedCouponId,
            }
          : {};
        const query = {
          areaId: this.areaId,
          price: this.cashParams.actualPrice,
        };
        this.$router.push({ name: "choose", params, query });
      } else {
        this.$router.push({ name: "Login" });
      }
    },
    // 使用优惠劵失败
    useCouponFail() {
      if (this.cashParams) {
        this.cashParams = {
          ...this.cashParams,
          selectedCouponId: DO_NOT_USE_COUPON,
        };
      }
    },
    formatStopData(stop) {
      if (!stop) return {};
      const { lng, lat } = stop;
      return {
        ...stop,
        point: { lng, lat },
      };
    },
    // 监听区域变化
    handleAreaChange(watch = true) {
      if (watch) {
        this.bookingDateTime = "";
      }
      const [hubStop] = this.hubStops || [];
      const stop = this.formatStopData(hubStop);
      if (this.isHubArea && hubStop) {
        if (this.disabledOnStop) {
          this.$MapService.setMapDataOnStop(stop);
        }
        if (this.disabledOffStop) {
          this.$MapService.setMapDataOffStop(stop);
        }
        this.$MapService.setMapRenderFlag();
      }
    },
    // 换向
    onSwitchDirection() {
      const { hubAreaInfo } =
        this.areas.find((item) => item.id === this.areaId) || {};
      const { reverseId } = hubAreaInfo || {};
      if (!reverseId) {
        this.$toast("不存在反方向区域");
        return;
      }
      let isOn = false;
      let stop = null;
      const index = this.areas.findIndex((item) => item.id === reverseId);
      if (index > -1) {
        const { stops } = this.areas[index] || {};
        if (this.isFromHub) {
          stop = this.offStopId
            ? stops.find((el) => el.id === this.offStopId)
            : null;
          isOn = true;
        } else {
          stop = this.onStopId
            ? stops.find((el) => el.id === this.onStopId)
            : null;
          isOn = false;
        }

        // 切换反方向区域
        this.onAreaPickerOk(null, index);
        // 自动回填上/下车站
        setTimeout(() => {
          if (stop) {
            if (isOn) {
              this.$MapService.setMapDataOnStop(this.formatStopData(stop));
            } else {
              this.$MapService.setMapDataOffStop(this.formatStopData(stop));
            }
            this.$MapService.setMapRenderFlag();
          }
        }, 100);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../assets/style/common.scss";
@import "./style.scss";
</style>
